/* eslint-disable @next/next/no-img-element */
import type { CSSProperties } from 'react';

/**
 * Roses are the in-app currency (formerly "coins"). The brand rose doubles as
 * its icon so the balance, prices and packs all read as the same thing.
 */
const ROSE_SRC = '/images/favicon/logo-64.webp';
const ROSE_SRC_LARGE = '/images/favicon/logo.webp';

interface RoseIconProps {
  className?: string;
  style?: CSSProperties;
}

export function RoseIcon({ className = 'h-4 w-4', style }: RoseIconProps) {
  return (
    <img
      src={ROSE_SRC}
      alt=""
      aria-hidden
      width={64}
      height={64}
      draggable={false}
      className={`inline-block shrink-0 object-contain ${className}`}
      style={style}
    />
  );
}

/** Pack sizes in ascending order; a bigger pack shows a fuller bouquet. */
const PACK_TIERS = [250, 700, 1600, 3500, 8000, 18000];

interface RoseBouquetProps {
  roses: number;
  className?: string;
}

export function RoseBouquet({
  roses,
  className = 'h-11 w-11',
}: RoseBouquetProps) {
  const tier = PACK_TIERS.findIndex((size) => roses <= size);
  const stems = tier === -1 ? PACK_TIERS.length : tier + 1;
  const spread = stems > 1 ? 56 / (stems - 1) : 0;

  return (
    <div
      aria-hidden
      className={`relative shrink-0 ${className}`}
      style={{ filter: 'drop-shadow(0 4px 10px rgba(236,72,153,0.35))' }}
    >
      {Array.from({ length: stems }, (_, i) => {
        const angle = stems > 1 ? -28 + i * spread : 0;
        const centre = Math.abs(i - (stems - 1) / 2);

        return (
          <img
            key={i}
            src={ROSE_SRC_LARGE}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              left: '50%',
              bottom: 0,
              width: stems > 1 ? '72%' : '100%',
              height: stems > 1 ? '72%' : '100%',
              objectFit: 'contain',
              transformOrigin: '50% 100%',
              transform: `translateX(-50%) rotate(${angle}deg) scale(${1 - centre * 0.06})`,
              zIndex: 10 - Math.round(centre * 2),
            }}
          />
        );
      })}
    </div>
  );
}
